import axios from "axios";
import React, { useState } from "react";
import { showErrorToast, showSuccessToast } from "../../utils/toaster";
import Verifyotp from "./Verifyotp";
import { FaSpinner } from "react-icons/fa";
import { API_URL } from "../../api";

function RegisterForm({
  showotpcomponent,
  setshowotpcomponent,
  closeLoginDialog,
}) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    userType: "B2B", // Default user type
    gstNumber: "", // Add gstNumber to state
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input changes for all fields
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value, // Dynamically set the value for the respective field
    }));
  };

  // Validation function
  const validateForm = () => {
    const { name, email, mobile, password, userType, gstNumber } = formData;

    // Check if name is provided
    if (!name.trim()) {
      showErrorToast("Full name is required.");
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email)) {
      showErrorToast("Please provide a valid email address.");
      return false;
    }

    // Validate mobile number (e.g., must be 10 digits)
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobile.trim() || !mobileRegex.test(mobile)) {
      showErrorToast("Please provide a valid 10-digit mobile number.");
      return false;
    }

    // Validate password (e.g., at least 8 characters, one uppercase, one number)
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
    if (!password.trim() || password.length < 8) {
      showErrorToast("Password must be at least 8 characters long.");
      return false;
    }

    // Validate GST number only if userType is B2B
    if (userType === "B2B") {
      if (!gstNumber.trim()) {
        showErrorToast("Please provide a GST number.");
        return false;
      }
    }

    return true; // All validations passed
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsSubmitting(true);

    // Validate form data
    if (!validateForm()) {
      setIsSubmitting(false);
      return; // Stop submission if validation fails
    }

    try {
      console.log("Form Data being submitted:", formData);
      const response = await axios.post(`${API_URL}/api/user/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        mobile: formData.mobile,
        type: formData.userType,
        gstNumber: formData.userType === "B2B" ? formData.gstNumber : undefined, // Only send GST Number if B2B
      });
      console.log("API response:", response.data);
      setshowotpcomponent(true);
      showSuccessToast("You're registered successfully! Please log in using your email and password.");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message?.toLowerCase() || "";

      if (errorMessage.includes("user already exists")) {
        showErrorToast("This Email ID is already registered.");
      } else if (errorMessage.includes("gst")) {
        showErrorToast("GST Number is required for B2B registration.");
      } else if (errorMessage.includes("required")) {
        showErrorToast("Please complete all the details before submitting.");
      } else {
        // Generic message without technical terms
        showErrorToast("We couldn’t complete your registration at the moment. Please try again shortly.");
      }

      // Developer debugging only (not visible to user)
      // console.log("Error during registration:", error.response?.data || error.message);
    }

    finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {showotpcomponent ? (
        <Verifyotp
          email={formData.email}
          closeLoginDialog={closeLoginDialog}
          setshowotpcomponent={setshowotpcomponent}
        />
      ) : (
        <form className="flex flex-col" onSubmit={handleSubmit}>
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700"
            >
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`mt-2 block w-full rounded-md border px-4 py-2 shadow-sm ${errors.name ? "border-red-500" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter your full name"
            />
            {errors.name && (
              <p className="text-red-500 text-xs">{errors.name}</p>
            )}
          </div>

          {/* Email Input */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`mt-2 block w-full rounded-md border px-4 py-2 shadow-sm ${errors.name ? "border-red-500" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter your Email Address"
            />
            {errors.email && (
              <p className="text-red-500 text-xs">{errors.email}</p>
            )}
          </div>

          {/* Mobile Input */}
          <div>
            <label
              htmlFor="mobile"
              className="block text-sm font-medium text-gray-700"
            >
              Mobile Number
            </label>
            <input
              type="number"
              id="mobile"
              pattern="\d*"
              maxLength="10"
              name="mobile"
              inputMode="numeric"
              value={formData.mobile}
              onChange={handleChange}
              className={`mt-2 block w-full rounded-md border px-4 py-2 shadow-sm ${errors.mobile ? "border-red-500" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter your mobile number"
            />
            {errors.mobile && (
              <p className="text-red-500 text-xs">{errors.mobile}</p>
            )}
          </div>

          {/* Password Input */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="mt-2 block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your password"
            />
          </div>

          {/* User Type Radio Buttons (B2B or B2C) */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              User Type
            </label>
            <div className="flex space-x-4 mt-2">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="b2b"
                  name="userType"
                  value="B2B"
                  checked={formData.userType === "B2B"}
                  onChange={handleChange}
                  // disabled={isSubmitting}
                  className="h-4 w-4 text-blue-500 focus:ring-blue-500"
                />
                <label htmlFor="b2b" className="ml-2 text-sm text-gray-700">
                  B2B
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="b2c"
                  name="userType"
                  value="B2C"
                  checked={formData.userType === "B2C"}
                  onChange={handleChange}
                  // disabled={isSubmitting}
                  className="h-4 w-4 text-blue-500 focus:ring-blue-500"
                />
                <label htmlFor="b2c" className="ml-2 text-sm text-gray-700">
                  B2C
                </label>
              </div>
            </div>
          </div>

          {/* GST Number Input - Only show if userType is B2B */}
          {formData.userType === "B2B" && (
            <div className="mt-2">
              <label
                htmlFor="gstNumber"
                className="block text-sm font-medium text-gray-700"
              >
                GST Number
              </label>
              <input
                type="text"
                id="gstNumber"
                name="gstNumber"
                value={formData.gstNumber}
                onChange={handleChange}
                // disabled={isSubmitting}
                className={`mt-2 block w-full rounded-md border px-4 py-2 shadow-sm ${errors.gstNumber ? "border-red-500" : "border-gray-300"
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Enter your GST number"
              />
              {errors.gstNumber && (
                <p className="text-red-500 text-xs">{errors.gstNumber}</p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-gradient-to-r mt-5 from-blue-500 to-indigo-600 text-white font-medium py-2 rounded-md shadow-md hover:from-indigo-600 hover:to-blue-500 transition-transform transform hover:scale-105 flex justify-center items-center"
          // disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="animate-spin text-2xl text-white mr-2" />
              </>
            ) : (
              "Register"
            )}
          </button>
        </form>
      )}
    </div>
  );
}

export default RegisterForm;
